import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { NgForOf, NgIf } from '@angular/common';
import { MatCardModule } from '@angular/material/card';

/** AI sparkle icon asset path - centralized for easy updates */
export const AI_SPARKLE_ICON = 'assets/icons/website-builder/ai-sparkle.svg';

/**
 * Import Analyzing Step Component
 *
 * Displays an animated AI analysis state with timeline showing progress
 * through each analysis step. Auto-advances through messages and emits
 * completion event when finished.
 *
 * @example
 * <app-import-analyzing-step
 *     [messages]="['Reading file...', 'Detecting columns...']"
 *     [stepDuration]="2000"
 *     (analysisComplete)="onAnalysisComplete()">
 * </app-import-analyzing-step>
 */
@Component({
    selector: 'app-import-analyzing-step',
    templateUrl: './import-analyzing-step.component.html',
    styleUrls: ['./import-analyzing-step.component.scss'],
    standalone: true,
    imports: [
        NgIf,
        NgForOf,
        MatCardModule,
    ],
})
export class ImportAnalyzingStepComponent implements OnInit, OnDestroy {
    /** AI sparkle icon path for template binding */
    readonly sparkleIcon = AI_SPARKLE_ICON;

    /** Array of analysis messages to display */
    @Input() messages: string[] = [
        'Reading file structure',
        'Detecting column types',
        'Matching to database fields',
        'Identifying tags & custom fields',
    ];

    /** trackBy for the timeline *ngFor (messages are a static ordered list). */
    trackByIndex(index: number): number {
        return index;
    }

    /** Duration in ms to show each step before advancing */
    @Input() stepDuration = 2000;

    /** Whether to auto-advance through steps */
    @Input() autoAdvance = true;

    /** Custom title text (defaults to "AI is analyzing your file...") */
    @Input() customTitle: string | null = null;

    /** Emitted when all analysis steps are complete */
    @Output() analysisComplete = new EventEmitter<void>();

    /** Currently active message index (0-based) */
    currentMessageIndex = 0;

    private _autoAdvanceTimer: ReturnType<typeof setTimeout> | null = null;

    ngOnInit(): void {
        if (this.autoAdvance) {
            this._startAutoAdvance();
        }
    }

    ngOnDestroy(): void {
        this._stopAutoAdvance();
    }

    private _startAutoAdvance(): void {
        this._advanceToNextStep();
    }

    private _advanceToNextStep(): void {
        this._autoAdvanceTimer = setTimeout(() => {
            if (this.currentMessageIndex < this.messages.length - 1) {
                this.currentMessageIndex++;
                this._advanceToNextStep();
            } else {
                // All steps complete - emit and let parent handle navigation
                this.analysisComplete.emit();
            }
        }, this.stepDuration);
    }

    private _stopAutoAdvance(): void {
        if (this._autoAdvanceTimer) {
            clearTimeout(this._autoAdvanceTimer);
            this._autoAdvanceTimer = null;
        }
    }

    getMessageState(index: number): 'completed' | 'active' | 'pending' {
        if (index < this.currentMessageIndex) {
            return 'completed';
        } else if (index === this.currentMessageIndex) {
            return 'active';
        }
        return 'pending';
    }
}
